import { Button, Stack, useTheme } from "@mui/material";
import { Box } from "@mui/system";
import { memo, useCallback, useEffect, useMemo } from "react";
import { Route, Routes, useNavigate, useLocation, Navigate } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import { clsx } from "clsx";

import { stepsPaths } from "../../constants";
import { StepPathType } from "../../shared";
import { ColorsPanel, DimensionsPanel, ItemsPanel, SectionsPanel, SpecificationPanel, ViewPanel } from "./Panels";
import { useCurrentSearchParam } from "../../utils/hooks/useCurrentSearchParam";
import { useWindowProperty } from "../../utils/hooks/useWidnowProperty";
import { useStoreSystem } from "../../store/useStoreSystem";
import VersionInfo from "../SceneObjects/VersionInfo/VersionInfo";
import styles from "./mainMenu.module.scss";
import shallow from "zustand/shallow";
import { StepsBlock } from "../StepsBlock/StepsBlock";
import { TireRacksPanel } from "./Panels/TireRacksPanel/TireRacksPanel";

const routes = {
    "/dimensions": "Габариты стеллажной системы | Конфигуратор ProMetal\n",
    "/sections": "Секции стеллажной системы | Конфигуратор ProMetal",
    "/items": "Наполнение стеллажной системы | Конфигуратор ProMetal",
    "/colors": "Цвета наполнения стеллажной системы | Конфигуратор ProMetal",
    "/specification": "Спецификация стеллажной системы | Конфигуратор ProMetal",
};

interface MainMenuProps {
    proMode: boolean;
}

export const MainMenu = memo(({ proMode }: MainMenuProps) => {
    const theme = useTheme();
    const matchesXL = useMediaQuery(theme.breakpoints.up("xl"));
    const windowDebug = useWindowProperty("debug", false);
    const { setLocation, settingsMenuIsVisible } = useStoreSystem(
        (state) => ({
            setLocation: state.setLocation,
            settingsMenuIsVisible: state.settingsMenuIsVisible,
        }),
        shallow
    );

    const navigate = useNavigate();
    const location = useLocation();
    const [hash, , hashForUrl] = useCurrentSearchParam("hash");
    const current = location.pathname.split("/")[1];
    const stepIndex = useMemo(() => stepsPaths.indexOf(current as StepPathType), [current]);

    useEffect(() => {
        setLocation(current);
    }, [current]);

    useEffect(() => {
        document.title = routes[location.pathname as keyof typeof routes] || "Конфигуратор ProMetal";
    }, [location]);

    const onNextClick = useCallback(() => {
        stepIndex !== -1 ? navigate(stepsPaths[stepIndex + 1] + hashForUrl) : navigate(stepsPaths[0] + hashForUrl);
    }, [stepIndex, navigate, hashForUrl, location]);

    return (
        <div className={clsx(styles.main, !settingsMenuIsVisible && styles.main_hide)}>
            <StepsBlock />
            <Stack
                direction="row"
                alignItems="center"
                minHeight="188px"
                maxHeight="90vh"
                px={matchesXL ? "95px" : "40px"}
                sx={{ background: "#fff" }}
            >
                <VersionInfo />
                <Box component={"div"} pr="20px" flexGrow={1}>
                    <Routes>
                        <Route path="dimensions" element={<DimensionsPanel proMode={proMode} />} />
                        <Route path="sections" element={<SectionsPanel />} />
                        <Route path="items" element={<ItemsPanel />} />
                        <Route path="stationary" element={<TireRacksPanel />} />
                        <Route path="colors" element={<ColorsPanel />} />
                        <Route path="view" element={<ViewPanel />} />
                        <Route
                            path="specification"
                            element={<SpecificationPanel proMode={proMode} windowDebug={windowDebug} />}
                        />
                        <Route path="*" element={<Navigate to={!hash ? `dimensions` : `dimensions?hash=${hash}`} />} />
                    </Routes>
                </Box>
                <Stack ml="auto" height="100%" justifyContent={"center"}>
                    {current !== "specification" && stepIndex !== -1 && (
                        <Button
                            variant="contained"
                            onClick={onNextClick}
                            sx={{ textTransform: "none", px: 5, py: 1.5, borderRadius: "10px" }}
                        >
                            Следующий шаг
                        </Button>
                    )}
                </Stack>
            </Stack>
        </div>
    );
});
